import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './MapComponent.css';

// Component that handles map reference for external control
function ReturnToLocationController({ mapRef }) {
  const map = useMap();

  useEffect(() => {
    if (map) {
      mapRef.current = map;
    }
  }, [map, mapRef]);

  return null;
}

// Component that auto-centers map on first GPS fix (MAP-01, MAP-02)
// Must be a child of <MapContainer> so useMap() resolves after mount — prevents race condition
function MapAutoCenter({ position, hasCenteredRef }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !position || hasCenteredRef.current) return;
    map.flyTo([position.lat, position.lng], 15, {
      animate: true,
      duration: 1.5,
      easeLinearity: 0.5
    });
    hasCenteredRef.current = true;
  }, [map, position, hasCenteredRef]);

  return null;
}

function MapComponent({ bases, userPosition }) {
  const mapRef = useRef(null);

  // Initial center: use localStorage last-known position if available, else Vancouver fallback
  const initialCenter = (() => {
    try {
      const saved = JSON.parse(localStorage.getItem('lastPosition'));
      if (saved && typeof saved.lat === 'number' && typeof saved.lng === 'number') {
        return [saved.lat, saved.lng];
      }
    } catch {}
    return [49.246292, -123.116226];
  })();
  const zoom = 5;

  // Ref guard: prevents flyTo from firing more than once (MAP-02)
  // useRef(false) — mutation of .current does NOT trigger re-render
  const hasCenteredRef = useRef(false);

  // Base marker component
  const BaseMarker = ({ base }) => {
    const isHome = base.name === 'Home';
    const isTrading = base.name === 'Trading Point';

    let color = 'brown';
    let icon = 'home';

    if (isTrading) {
      color = '#FF9800';
      icon = 'storefront';
    } else if (isHome) {
      color = '#2196F3';
      icon = 'home';
    }

    return (
      <Marker
        position={[base.location.lat, base.location.lng]}
        icon={L.divIcon({
          html: `<div style="background-color: ${color}; border-radius: 50%; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"><i class="material-icons" style="color: white; font-size: 20px;">${icon}</i></div>`,
          className: 'base-marker',
          iconSize: [36, 36],
          iconAnchor: [18, 18]
        })}
      />
    );
  };

  // Current position marker
  const CurrentPositionMarker = ({ position }) => {
    if (!position) return null;
    return (
      <Marker
        position={[position.lat, position.lng]}
        icon={L.divIcon({
          html: '<div style="background-color: #4CAF50; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; box-shadow: 0 0 0 4px rgba(76, 175, 80, 0.3);"><div style="background-color: white; border-radius: 50%; width: 10px; height: 10px;"></div></div>',
          className: 'current-marker',
          iconSize: [24, 24],
          iconAnchor: [12, 12]
        })}
      />
    );
  };

  // Handle returning to location
  const handleReturnToLocation = () => {
    if (mapRef.current && userPosition) {
      mapRef.current.flyTo([userPosition.lat, userPosition.lng], 15, {
        animate: true,
        duration: 1.5,
        easeLinearity: 0.5
      });
    }
  };

  return (
    <div className="map-container">
      <MapContainer
        center={initialCenter}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
      >
        <ReturnToLocationController mapRef={mapRef} />
        <MapAutoCenter position={userPosition} hasCenteredRef={hasCenteredRef} />
        <ZoomControl position="topright" />
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        {bases.map((base, index) => (
          <BaseMarker key={index} base={base} />
        ))}
        <CurrentPositionMarker position={userPosition} />
        {userPosition && userPosition.accuracy > 50 && (
          <Circle
            center={[userPosition.lat, userPosition.lng]}
            radius={userPosition.accuracy}
            pathOptions={{
              color: '#4CAF50',
              fillColor: '#4CAF50',
              fillOpacity: 0.1,
              weight: 1,
              opacity: 0.4
            }}
          />
        )}
      </MapContainer>

      {/* Return to location button - positioned in overlay space */}
      <button
        className={`return-to-location-button ${!userPosition ? 'loading' : ''}`}
        onClick={handleReturnToLocation}
        disabled={!userPosition}
        title={userPosition ? "Return to current location" : "Locating..."}
      >
        <i className={`material-icons ${!userPosition ? 'spin' : ''}`}>
          {userPosition ? 'my_location' : 'gps_not_fixed'}
        </i>
      </button>
    </div>
  );
}

export default MapComponent;
